import {
  forwardRef,
  ComponentPropsWithoutRef,
  MouseEvent,
  KeyboardEvent,
} from "react";
import cx from "classnames";
import { Icon, IconProps } from "../Icon";
import styles from "./Button.module.scss";
import { ButtonAppearance, LayoutUtilProps, Size } from "../../types";
import { Spinner } from "../Spinner";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the ButtonLink component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"a">
 */
export type ButtonLinkProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"a"> & {
    /**
     * The disabled state of the button link.
     * @default false
     */
    disabled?: boolean;

    /**
     * The visual variant of the button link.
     * @default secondary
     */
    appearance?: ButtonAppearance;

    /**
     * The size of the button link.
     * @default medium
     */
    size?: Extract<Size, "small" | "medium" | "large">;

    /**
     * The icons of the button link.
     */
    icon?:
      | IconProps["svg"]
      | {
          after: IconProps["svg"];
        }
      | {
          before: IconProps["svg"];
        };

    /**
     * The loading state of the button link.
     * If true, it will show infinite state of the loading.
     * @default false
     */
    loading?: boolean;
  };

/**
 * ButtonLink component for creating clickable links that look like buttons.
 *
 * Features:
 * - Multiple visual appearances (primary, secondary, ghost, danger variants)
 * - Three size options (small, medium, large)
 * - Loading state with spinner animation
 * - Icon support (before, after, or icon-only)
 * - Full accessibility support with proper ARIA attributes
 * - Supports layout utilities for positioning and spacing
 * - Keyboard navigation support (Space and Enter keys)
 * - Automatic disabled state when loading
 * - Prevents navigation when disabled or loading
 * - Flexible content with text and/or icons
 *
 * @example
 * <ButtonLink href="/dashboard" appearance="primary" size="medium">
 *   Go to Dashboard
 * </ButtonLink>
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      appearance = "secondary",
      size = "medium",
      icon,
      loading = false,
      disabled,
      onClick,
      onKeyUp,
      onKeyDown,
      style,
      ...rest
    } = componentProps;

    const buttonClassNames = cx(className, styles["button"], {
      [styles["appearance-primary"]]: appearance === "primary",
      [styles["appearance-secondary"]]: appearance === "secondary",
      [styles["appearance-ghost"]]: appearance === "ghost",
      [styles["danger-secondary"]]: appearance === "danger-secondary",
      [styles["danger-primary"]]: appearance === "danger",
      [styles["size-small"]]: size === "small",
      [styles["size-medium"]]: size === "medium",
      [styles["size-large"]]: size === "large",
      [styles["type-icon"]]: !children,
      [styles["loading"]]: loading,
    });

    const iconBefore = (icon: ButtonLinkProps["icon"]) => {
      if (icon && typeof icon === "object" && "before" in icon) {
        return (
          <Icon
            aria-hidden
            className={styles["icon"]}
            inherit
            svg={icon.before}
          />
        );
      }
      if (icon && typeof icon !== "object") {
        return (
          <Icon aria-hidden className={styles["icon"]} inherit svg={icon} />
        );
      }
      return;
    };

    const handleOnClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.code === "Space") {
        e.preventDefault();
        onKeyDown?.(e);
        return;
      }

      onKeyDown?.(e);
    };

    const onKeyUpHandler = (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        if (onClick) {
          e.currentTarget.click();
        }
      }

      onKeyUp?.(e);
    };

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <a
        role="link"
        className={buttonClassNames}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        data-anv="button-link"
        tabIndex={disabled || loading ? -1 : 0}
        onClick={handleOnClick}
        onKeyDown={onKeyDownHandler}
        onKeyUp={onKeyUpHandler}
        style={styleCombined}
        ref={ref}
        {...rest}
      >
        {loading ? (
          <Spinner inherit className={styles["loading-spinner"]} />
        ) : icon ? (
          iconBefore(icon)
        ) : null}
        {children}
        {typeof icon === "object" && "after" in icon ? (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon.after}
          />
        ) : null}
      </a>
    );
  },
);

ButtonLink.displayName = "ButtonLink";
